import ExcelJS from 'exceljs';

/**
 * Excel export for a single claim (per diem or expense).
 *
 * Admins already had a PDF of a claim; this gives the same content in a form
 * that can be fed into payroll rather than re-keyed. Uses ExcelJS, matching the
 * monthly report export.
 */

const CARDINAL = 'FF8C1515';

const styleHeaderRow = (row) => {
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CARDINAL } };
  row.alignment = { vertical: 'middle' };
};

const autoWidth = (worksheet, widths) => {
  widths.forEach((w, i) => { worksheet.getColumn(i + 1).width = w; });
};

const fmtDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
};

const download = async (workbook, filename) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Group a claim's lines by the PTAE funding them.
 *
 * `allocation.linePtae` is a plain { [lineId]: code } object rather than a Map,
 * so either page can build it and it survives being passed around. Lines with
 * no code are collected under a final "Unassigned" bucket instead of being
 * dropped - a line nobody funded is exactly what a reviewer needs to see.
 */
const groupByPtae = (lines, allocation, valueOf) => {
  const UNASSIGNED = '~~unassigned';
  const order = [];
  const buckets = new Map();

  const place = (code, line, amount, part) => {
    const key = code || UNASSIGNED;
    if (!buckets.has(key)) {
      buckets.set(key, { code, lines: [], total: 0 });
      order.push(key);
    }
    const b = buckets.get(key);
    b.lines.push({ line, amount, part });
    b.total += amount;
  };

  for (const line of lines) {
    // A line split between PTAEs goes under EACH of them with that PTAE's
    // part, so every subtotal is what that award actually pays - the same as
    // the emailed workbook (backend utils/claimBundle.mjs).
    const parts = (allocation?.lineParts || {})[String(line.id)];
    if (parts && parts.length > 1) {
      for (const p of parts) place(p.code, line, Number(p.amount) || 0, true);
    } else {
      place((allocation?.linePtae || {})[String(line.id)] || '', line, valueOf(line), false);
    }
  }

  // Unassigned last, so the funded work reads first.
  order.sort((a, b) => (a === UNASSIGNED ? 1 : b === UNASSIGNED ? -1 : 0));
  return order.map(k => buckets.get(k));
};

/**
 * A sheet that restates the claim one PTAE at a time.
 *
 * The main sheet is chronological, which is how the claim was filed but not how
 * it is funded. Finance need the opposite view - what does each code carry -
 * and deriving that by hand from a 30-row trip is exactly the error-prone step
 * the line-item split was meant to remove.
 */
const addByPtaeSheet = (workbook, claim, lines, allocation, columns, rowFor, valueOf, widths) => {
  if (!lines.length) return;
  const ws = workbook.addWorksheet('By PTAE');

  ws.addRow(['ALLOCATION BY PTAE']).font = { bold: true, size: 14, color: { argb: CARDINAL } };
  ws.addRow(['Claimant', `${claim?.user?.firstName || ''} ${claim?.user?.lastName || ''}`.trim() || '-']);
  ws.addRow(['Claim ID', claim?.id || claim?._id || '-']);
  ws.addRow([]);

  const groups = groupByPtae(lines, allocation, valueOf);
  const pad = new Array(Math.max(0, columns.length - 2)).fill('');
  let grand = 0;

  for (const g of groups) {
    const head = ws.addRow([g.code ? `PTAE ${g.code}` : 'UNASSIGNED - no PTAE on these lines']);
    head.font = { bold: true, color: { argb: g.code ? CARDINAL : 'FF9A3412' } };

    styleHeaderRow(ws.addRow(columns));
    for (const { line, amount, part } of g.lines) {
      const cells = rowFor(line);
      // The PTAE is the last column; for a part it says how much of the line.
      if (part) {
        const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        cells[cells.length - 1] = `${g.code} - part: ${fmt(amount)} of ${fmt(valueOf(line))}`;
      }
      ws.addRow(cells);
    }

    const sub = ws.addRow([
      ...pad,
      `Subtotal - ${g.lines.length} line${g.lines.length === 1 ? '' : 's'}`,
      g.total,
    ]);
    sub.font = { bold: true };
    ws.addRow([]);
    grand += g.total;
  }

  const total = ws.addRow([...pad, 'TOTAL (USD)', grand]);
  total.font = { bold: true, size: 12 };
  autoWidth(ws, widths);
};

const addClaimHeader = (worksheet, claim, title) => {
  worksheet.addRow([title]).font = { bold: true, size: 14, color: { argb: CARDINAL } };
  worksheet.addRow([]);
  worksheet.addRow(['Claimant', `${claim?.user?.firstName || ''} ${claim?.user?.lastName || ''}`.trim() || '-']);
  worksheet.addRow(['Email', claim?.user?.email || '-']);
  worksheet.addRow(['Region', claim?.user?.region || '-']);
  worksheet.addRow(['Status', claim?.status || '-']);
  worksheet.addRow(['Claim ID', claim?.id || claim?._id || '-']);
  worksheet.addRow(['Exported', new Date().toLocaleString('en-US')]);
  worksheet.addRow([]);
};

/** Per diem claim → .xlsx */
export const exportPerDiemClaimToExcel = async (claim, allocation = null) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Stanford Seed Expense Claim System';
  workbook.created = new Date();
  const ws = workbook.addWorksheet('Per Diem Claim');

  addClaimHeader(ws, claim, 'PER DIEM CLAIM');
  ws.addRow(['Business Purpose', claim?.reasonForTrip || claim?.businessPurpose || '-']);
  ws.addRow(['Travel Dates', `${fmtDate(claim?.startDate)} - ${fmtDate(claim?.endDate)}`]);
  ws.addRow([]);

  // PTAE is emitted ALWAYS, even when nothing is allocated, so the column set
  // is stable between claims and stays identical to the server-side workbook.
  const PD_COLUMNS = [
    'Date', 'City', 'Country', 'Breakfast', 'Lunch', 'Dinner',
    'Incidentals', 'Total M&IE', 'Lodging', 'Daily Total', 'PTAE',
  ];
  const PD_WIDTHS = [14, 22, 18, 12, 12, 12, 12, 12, 12, 14, 26];
  const pdValue = (i) =>
    (Number(i.totalMIE) || 0) + (Number(i.lodgingCost ?? i.lodgingActual ?? 0) || 0);
  const pdRow = (item) => [
    fmtDate(item.date),
    item.city || '',
    item.country || '',
    Number(item.breakfast) || 0,
    Number(item.lunch) || 0,
    Number(item.dinner) || 0,
    Number(item.incidentals) || 0,
    Number(item.totalMIE) || 0,
    Number(item.lodgingCost ?? item.lodgingActual ?? 0) || 0,
    pdValue(item),
    (allocation?.linePtae || {})[String(item.id)] || '',
  ];

  styleHeaderRow(ws.addRow(PD_COLUMNS));

  const lines = claim?.dailyDetails || claim?.lineItems || [];
  for (const item of lines) ws.addRow(pdRow(item));

  const total = lines.reduce(
    (sum, i) => sum + (Number(i.totalMIE) || 0) + (Number(i.lodgingCost ?? i.lodgingActual ?? 0) || 0),
    0
  );
  ws.addRow([]);
  // Padding is COMPUTED, not hardcoded: the server-side workbook derives its own
  // from columns.length, so a fixed run of blanks here would put TOTAL in a
  // different cell the moment a column is added - and the emailed workbook and
  // this one are required to match cell for cell.
  const totalRow = ws.addRow([
    ...new Array(Math.max(0, PD_COLUMNS.length - 2)).fill(''),
    'TOTAL (USD)',
    total,
  ]);
  totalRow.font = { bold: true };

  autoWidth(ws, PD_WIDTHS);

  addByPtaeSheet(workbook, claim, lines, allocation, PD_COLUMNS, pdRow, pdValue, PD_WIDTHS);

  const who = `${claim?.user?.lastName || 'claim'}`.replace(/\s+/g, '_');
  await download(workbook, `PerDiem_${who}_${claim?.id || ''}.xlsx`);
};

/** Expense claim → .xlsx */
export const exportExpenseClaimToExcel = async (claim, allocation = null) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Stanford Seed Expense Claim System';
  workbook.created = new Date();
  const ws = workbook.addWorksheet('Expense Claim');

  addClaimHeader(ws, claim, 'EXPENSE REIMBURSEMENT CLAIM');

  const EX_COLUMNS = [
    'Date', 'Receipt #', 'Location', 'Description', 'Currency',
    'Amount', 'Exchange Rate', 'USD Equivalent', 'Comments', 'PTAE',
  ];
  const EX_WIDTHS = [14, 16, 20, 34, 10, 14, 14, 16, 28, 26];
  const exValue = (i) => Number(i.usdEquivalent) || 0;
  const exRow = (item) => [
    fmtDate(item.expenditureDate),
    item.receiptNumber || '',
    item.expenditureLocation || '',
    item.expenditureDescription || '',
    item.expenditureCurrency || '',
    Number(item.receiptAmount) || 0,
    Number(item.exchangeRate) || 0,
    Number(item.usdEquivalent) || 0,
    item.comments || '',
    (allocation?.linePtae || {})[String(item.id)] || '',
  ];

  styleHeaderRow(ws.addRow(EX_COLUMNS));

  const items = claim?.items || [];
  for (const item of items) ws.addRow(exRow(item));

  const total = items.reduce((sum, i) => sum + (Number(i.usdEquivalent) || 0), 0);
  ws.addRow([]);
  const totalRow = ws.addRow([
    ...new Array(Math.max(0, EX_COLUMNS.length - 2)).fill(''),
    'TOTAL (USD)',
    total,
  ]);
  totalRow.font = { bold: true };

  autoWidth(ws, EX_WIDTHS);

  addByPtaeSheet(workbook, claim, items, allocation, EX_COLUMNS, exRow, exValue, EX_WIDTHS);

  const who = `${claim?.user?.lastName || 'claim'}`.replace(/\s+/g, '_');
  await download(workbook, `Expense_${who}_${claim?.id || ''}.xlsx`);
};

const claimExcel = { exportPerDiemClaimToExcel, exportExpenseClaimToExcel };
export default claimExcel;
